import { ITenantTemplateAiSettings } from "./ApiHelper";
import { DatasourceTypeEnum } from "../../data-structures/enums";

/**
 * The dropdown key standing for "no stored choice, let the API infer".
 *
 * A sentinel rather than `null` because Fluent's IDropdownOption.key is `string | number`, so
 * null cannot BE an option — and without an option there is no way to select it. That was the
 * bug: once an admin picked a datasource, `crmDatasourceId` could never go back to NULL from
 * the portal, even though NULL is the documented default and the state the migration, the DTO
 * and TemplateAiVocabulary.Assemble all treat as first-class. A single-select Fluent Dropdown
 * has no deselect gesture, and toggling AI off and on again preserves the value.
 *
 * -1 is safe: Datasource.Id is an IDENTITY column, so no real datasource can collide with it.
 */
export const TEMPLATE_AI_INFER_KEY = -1;

export interface ITemplateAiSectionState {
    showToggle: boolean;
    toggleDisabled: boolean;
    /** The tenant is switched on but has lost (or never had) a document template library. */
    showNoTemplateLibraryWarning: boolean;
    /** The tenant is switched on but the SERVER cannot run the feature at all. */
    showFeatureUnavailableWarning: boolean;
    showCrmPicker: boolean;
    /**
     * The readiness summary. Requires a HEALTHY section, not merely a live toggle.
     *
     * Its own flag rather than `showToggle && !toggleDisabled` at the call site, because that
     * expression is true in both escape-hatch states — a tenant switched on with no server
     * feature, or with no template library, keeps a live toggle so the flag can be cleared.
     * The summary would then have claimed "✓ Custom fields, ✓ Document fields" directly
     * beneath a warning saying the feature cannot run at all, which is worse than silence.
     */
    showCapabilities: boolean;
}

const HIDDEN: ITemplateAiSectionState = {
    showToggle: false,
    toggleDisabled: true,
    showNoTemplateLibraryWarning: false,
    showFeatureUnavailableWarning: false,
    showCrmPicker: false,
    showCapabilities: false,
};

/**
 * Decides what the AI-suggestions section renders for one tenant.
 *
 * The section is gated on the tenant owning at least one SharePointDocumentTemplate
 * datasource, because the add-in command that consumes the flag cannot render without one
 * (isTemplateDatasourceLocation) — offering the toggle to anyone else is offering a control
 * that provably does nothing.
 *
 * There are TWO such gates, and they are the same shape one level apart:
 *
 *  - `featureAvailable` — the SERVER's TemplateAi section is enabled and carries an Endpoint
 *    and an ApiKey. False means no orchestrator is registered, so the flag cannot do anything
 *    for ANY tenant. Checked FIRST: it is a property of the deployment, and there is no point
 *    telling an admin to connect a datasource for a feature the server cannot run.
 *  - `hasTemplateDatasource` — this tenant has somewhere for the command to appear.
 *
 * Neither may simply HIDE a tenant that is switched on: that would strand
 * TemplateAiEnabled = true in the database with no way to see or clear it, and that flag is
 * the consent gate for template text leaving the infrastructure. So in both cases the toggle
 * stays live with a warning, the admin can switch it off, and the section stays put until the
 * panel is reopened — see `wasSavedOn`.
 *
 * `enabled` is passed in rather than read off `settings`: the flag is an ordinary tenant
 * setting living on the panel's draft tenant, so the section must react to the DRAFT — switch
 * the toggle on and the picker has to appear immediately, not after a save and a refetch.
 *
 * `wasSavedOn` is the flag as the panel OPENED it, and it is what keeps both escape hatches on
 * screen. Keying them off `enabled` alone meant the section deleted itself the moment the admin
 * did what the warning asked — taking the toggle, the only control for the flag, with it. The
 * saved flag is fixed for the panel's lifetime, so it latches the section open for the whole
 * editing session without any new state, and resets by construction when the panel reopens for
 * another tenant. It is deliberately NOT `||`-ed into the healthy branch: `showToggle` is
 * already unconditional there, and the picker and summary must keep keying off `enabled` alone.
 */
export const templateAiSectionState = (
    settings: ITenantTemplateAiSettings | undefined,
    isUnsavedTenant: boolean,
    enabled: boolean,
    wasSavedOn: boolean): ITemplateAiSectionState => {

    // undefined = still loading, or the load failed. Either way there is nothing to gate on
    // yet, so keep the previous behaviour: visible, inert.
    if (settings === undefined || isUnsavedTenant) {
        return { ...HIDDEN, showToggle: true, toggleDisabled: true };
    }

    if (!settings.featureAvailable) {
        return (enabled || wasSavedOn)
            ? { ...HIDDEN, showToggle: true, toggleDisabled: false, showFeatureUnavailableWarning: true }
            : HIDDEN;
    }

    if (!settings.hasTemplateDatasource) {
        return (enabled || wasSavedOn)
            ? { ...HIDDEN, showToggle: true, toggleDisabled: false, showNoTemplateLibraryWarning: true }
            : HIDDEN;
    }

    // The picker exists only to break a tie: with zero or one CRM family there is nothing to
    // choose, and vocabulary assembly infers the same answer either way.
    const families = new Set((settings.crmDatasources ?? []).map(d => d.typeId));

    // Only reachable once BOTH hard gates have passed, which is exactly the condition the
    // summary needs: every earlier return is a HIDDEN spread, so showCapabilities stays false
    // in both escape-hatch states without the call site having to remember that.
    return {
        showToggle: true,
        toggleDisabled: false,
        showNoTemplateLibraryWarning: false,
        showFeatureUnavailableWarning: false,
        showCrmPicker: enabled && families.size > 1,
        showCapabilities: enabled,
    };
};

/**
 * The picker's options, INFER FIRST.
 *
 * Kept out of the JSX so the "there is always a way back to infer" rule is testable rather
 * than being a property of a render tree. `inferText` is passed in because this module is
 * localisation-agnostic; the caller supplies languageStrings.
 */
export const templateAiCrmOptions = (
    settings: ITenantTemplateAiSettings | undefined,
    inferText: string): { key: number; text: string }[] => [
        { key: TEMPLATE_AI_INFER_KEY, text: inferText },
        ...(settings?.crmDatasources ?? []).map(d => ({
            key: d.id,
            // The type name disambiguates two datasources that share a name, which is the
            // whole reason a tie needs breaking. Unknown ids fall back to the raw number
            // rather than rendering "undefined".
            text: `${d.name} (${DatasourceTypeEnum[d.typeId] ?? d.typeId})`,
        })),
    ];

/** A prerequisite the tenant is missing, in the order the summary lists them. */
export type TemplateAiPrerequisite = "crmDatasource" | "clientList";

export interface ITemplateAiCapability {
    /** Stable key; the caller maps it to a localised label. */
    key: "custom" | "builtin" | "client";
    available: boolean;
    /** What is missing, in a stable order. Empty when available. */
    missing: TemplateAiPrerequisite[];
}

/**
 * What AI suggestions can actually offer this tenant — the portal's readiness summary.
 *
 * A capability LIST rather than another warning bar, for three reasons:
 *
 *  - It is complete. The add-in's note used to surface one missing prerequisite at a time, so
 *    an author connected a CRM datasource, re-ran, and only then learned a client list was
 *    needed too. Listing every row at once removes the round trip rather than reordering it.
 *  - It says what WORKS, not only what does not. `custom` and `builtin` need no tenant
 *    configuration at all, and custom fields are plausibly the most valuable family — a lone
 *    warning reads as "this feature is broken" when it is merely partial.
 *  - It sits where the fixes are. The client-list URL is a field in this same panel and
 *    Datasources is one button away; the add-in, which reports the gap today, is used by a
 *    template author who usually cannot change either.
 *
 * `hasClientListUrl` comes from the DRAFT tenant rather than the server, so typing a client
 * list URL into the Accountancy section flips the client row before anything is saved. That
 * is a preview of the configuration being edited, which is what an admin mid-edit wants — and
 * it matches the CRM picker, which already reacts to the draft toggle.
 *
 * Returns [] while the metadata is still loading: `custom` and `builtin` would be honest, but
 * the client row would flash a prerequisite that may well be satisfied.
 */
export const templateAiCapabilities = (
    settings: ITenantTemplateAiSettings | undefined,
    hasClientListUrl: boolean): ITemplateAiCapability[] => {

    if (settings === undefined) return [];

    const missing: TemplateAiPrerequisite[] = [];
    // Both prerequisites are genuinely required: the CRM mappers supply the labels, and
    // without a client list the resolvers have nothing to match a destination site against,
    // so every CRM token would ship unfilled.
    if (!(settings.crmDatasources?.length > 0)) missing.push("crmDatasource");
    if (!hasClientListUrl) missing.push("clientList");

    return [
        // Self-describing: the token carries its own name and type, and the value is collected
        // from the user at creation time. Nothing tenant-level can take these away.
        { key: "custom", available: true, missing: [] },
        // Document metadata (Created, Author). Likewise unconditional.
        { key: "builtin", available: true, missing: [] },
        { key: "client", available: missing.length === 0, missing },
    ];
};

/** Stored value → selected key. A cleared (null/undefined) choice selects "infer". */
export const templateAiSelectedCrmKey = (crmDatasourceId: number | null | undefined): number =>
    crmDatasourceId ?? TEMPLATE_AI_INFER_KEY;

/**
 * Selected key → the value stored on the tenant. The infer sentinel must never be persisted
 * as an id: -1 reaching the database would be a stale id forever, and assembly would report
 * the choice as "no longer configured" on every run.
 *
 * Returns `undefined` rather than `null`, and that is load-bearing. These settings live on
 * `newTenant` now, so EditTenantPanel's generic isDirty() walk compares them — and that walk
 * branches on `typeof value != 'object'`. Since `typeof null === 'object'`, a null would skip
 * the scalar comparison and fall through to an unrelated partner check, so clearing the picker
 * back to "infer" would silently fail to arm the Save button. `undefined` takes the scalar
 * branch; the walk's loose `!=` then treats it as equal to a stored null, so an untouched
 * panel still compares clean. JSON omits the field and the API reads it as null.
 */
export const templateAiCrmKeyToStoredId = (key: string | number | undefined): number | undefined =>
    typeof key === "number" && key !== TEMPLATE_AI_INFER_KEY ? key : undefined;
